export const PERMISSIONS = {
  DASHBOARD_READ: 'dashboard:read:tenant',
  JOB_READ: 'job:read:resource',
  DATASET_READ: 'dataset:read:resource',
  DATASET_CREATE: 'dataset:create:user',
  DATASET_DOWNLOAD: 'dataset:download:resource',
  DATASET_UPDATE: 'dataset:update:resource',
  DATASET_ASSIGN: 'dataset:assign:resource',
  USER_READ: 'user:read:user',
  USER_CREATE: 'user:create:tenant',
  USER_UPDATE: 'user:update:user',
  USER_UPDATE_TENANT: 'user:update:tenant',
  ROLE_MANAGE: 'role:manage:tenant',
  PERMISSION_READ: 'permission:read:tenant',
  PERMISSION_MANAGE: 'permission:manage:tenant',
  SESSION_READ: 'session:read:tenant',
  SESSION_REVOKE: 'session:revoke:tenant',

  // Fine-grained RBAC Permission Keys
  UPLOAD_VIEW: 'upload:view:tenant',
  UPLOAD_CREATE: 'upload:create:tenant',
  ORIGINAL_VIEW: 'original:view:resource',
  ORIGINAL_DOWNLOAD: 'original:download:resource',
  PROTECTED_VIEW: 'protected:view:resource',
  PROTECTED_DOWNLOAD: 'protected:download:resource',
  PROCESS_START: 'process:start:resource',
  PROCESS_VIEW: 'process:view:resource',
  FILE_DELETE: 'file:delete:resource',
  DATASET_EXPORT: 'dataset:export:tenant',
  USER_MANAGE: 'user:manage:tenant',
  AUDIT_VIEW: 'audit:view:tenant',

  // Generic Data Vault
  SOURCE_READ: 'source:read:tenant',
  SOURCE_MANAGE: 'source:manage:tenant',
  VAULT_READ: 'vault:read:resource',
  VAULT_EXPORT: 'vault:export:tenant',
  VAULT_REVEAL_PII: 'vault:reveal:resource',

  // Freelancer Data Acquisition & Client Catalog
  AGREEMENT_READ: 'agreement:read:tenant',
  AGREEMENT_SIGN: 'agreement:sign:user',
  SUBMISSION_CREATE: 'submission:create:user',
  SUBMISSION_READ_MINE: 'submission:read:user',
  SUBMISSION_SUPERADMIN_READ: 'submission:read:system',
  SUBMISSION_SUPERADMIN_ASSIGN: 'submission:assign:system',
  SUBMISSION_QC_REVIEW: 'submission:qc:resource',
  RIGHTS_VERIFY: 'rights:verify:system',
  CATALOG_PUBLISH_CLIENT: 'catalog:publish:system',
  CLIENT_CATALOG_READ: 'catalog:read:tenant',

  // Retail Labeled Dataset Platform
  RETAIL_READ: 'retail:read:tenant',
  RETAIL_UPLOAD: 'retail:upload:tenant',
  RETAIL_QC_REVIEW: 'retail:qc:resource',
} as const;

export const PERMISSION_SCOPES = ['system', 'tenant', 'project', 'resource', 'user'] as const;
export type PermissionScope = typeof PERMISSION_SCOPES[number];

export const LEGACY_PERMISSION_ALIASES: Readonly<Record<string, string>> = {
  'dashboard:view': PERMISSIONS.DASHBOARD_READ,
  'jobs:read': PERMISSIONS.JOB_READ,
  'media:read': PERMISSIONS.DATASET_READ,
  'media:upload': PERMISSIONS.DATASET_CREATE,
  'media:download': PERMISSIONS.DATASET_DOWNLOAD,
  'media:reprocess': PERMISSIONS.DATASET_UPDATE,
  'media:assign': PERMISSIONS.DATASET_ASSIGN,
  'users:read': PERMISSIONS.USER_READ,
  'users:create': PERMISSIONS.USER_CREATE,
  'users:manage': PERMISSIONS.USER_UPDATE,
  'users:manage_privileged': PERMISSIONS.USER_UPDATE_TENANT,
  'permissions:read': PERMISSIONS.PERMISSION_READ,
  'permissions:manage': PERMISSIONS.PERMISSION_MANAGE,
};

export function canonicalPermission(permission: string): string {
  const normalized = permission.trim().toLocaleLowerCase();
  return LEGACY_PERMISSION_ALIASES[normalized] ?? normalized;
}

export function permissionScope(permission: string): PermissionScope | undefined {
  const scope = canonicalPermission(permission).split(':')[2];
  return PERMISSION_SCOPES.find((value) => value === scope);
}
